import { useRef } from "react";
import { useTranslation } from "@/contexts/LanguageContext";
import { Camera, Printer, Crop, Eye, FilmStrip } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { usePDFExport } from "@/utils/pdfExport";

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.05 } },
};
const itemAnim = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

// 8 standard shots, ordered tightest -> widest.
// crop_y / crop_h define the visible band of the silhouette in the diagram
// (the silhouette spans y=10..390 in a 200-wide x 400-tall canvas).
const SHOTS = [
  {
    key: "extreme_close_up",
    photo: "/shots/extreme_close_up.png",
    crop: { y: 38, h: 36 },   // eyes / mouth area
  },
  {
    key: "close_up",
    photo: "/shots/close_up.png",
    crop: { y: 28, h: 70 },   // full face
  },
  {
    key: "medium_close_up",
    photo: "/shots/medium_close_up.png",
    crop: { y: 28, h: 130 },  // chest up
  },
  {
    key: "medium_shot",
    photo: "/shots/medium_shot.png",
    crop: { y: 28, h: 200 },  // waist up
  },
  {
    key: "medium_long_shot",
    photo: "/shots/medium_long_shot.png",
    crop: { y: 28, h: 280 },  // knees up
  },
  {
    key: "long_shot",
    photo: "/shots/long_shot.png",
    crop: { y: 18, h: 380 },  // full body
  },
  {
    key: "wide_shot",
    photo: "/shots/wide_shot.png",
    crop: { y: 0, h: 400 },   // full body + lots of environment
    showsEnvironment: 0.5,
  },
  {
    key: "extreme_wide_shot",
    photo: "/shots/extreme_wide_shot.png",
    crop: { y: 0, h: 400 },
    showsEnvironment: 0.85,
  },
];

// SVG: silhouette with crop window overlay showing the shot framing.
function ShotDiagram({ shot, dark = true }) {
  const bg = dark ? "#0A0A0A" : "#F4F4F5";
  const figureFill = dark ? "#3F3F46" : "#A1A1AA";
  const figureStroke = dark ? "#52525B" : "#71717A";
  const ground = dark ? "#18181B" : "#E4E4E7";
  const cropStroke = "#F5A524";
  const cropFill = "rgba(245, 165, 36, 0.12)";
  const env = shot.showsEnvironment || 0;

  // Frame is 200 x 400 (tall), the figure is centered.
  // Crop window is a horizontal band that simulates the shot's visible area.
  return (
    <svg viewBox="0 0 200 400" className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
      {/* Frame background */}
      <rect x="0" y="0" width="200" height="400" fill={bg} />

      {/* Environment hint for wide / extreme wide shots */}
      {env > 0 && (
        <>
          {/* sky */}
          <rect x="0" y="0" width="200" height={300 - env * 80} fill={dark ? "#18181B" : "#F4F4F5"} opacity="0.6" />
          {/* mountains */}
          <polygon
            points={`0,${300 - env * 60} 50,${260 - env * 70} 100,${290 - env * 50} 150,${250 - env * 70} 200,${290 - env * 60} 200,300 0,300`}
            fill={dark ? "#27272A" : "#D4D4D8"}
          />
          {/* ground */}
          <rect x="0" y="300" width="200" height="100" fill={ground} />
        </>
      )}
      {env === 0 && (
        <rect x="0" y="380" width="200" height="20" fill={ground} />
      )}

      {/* Figure (simplified person silhouette) */}
      <g
        transform={
          env > 0
            ? `translate(${env >= 0.85 ? 88 : 75}, ${env >= 0.85 ? 250 : 210}) scale(${env >= 0.85 ? 0.18 : 0.42})`
            : "translate(50, 10)"
        }
      >
        {/* head */}
        <circle cx="50" cy="25" r="22" fill={figureFill} stroke={figureStroke} strokeWidth="1.5" />
        {/* neck */}
        <rect x="42" y="44" width="16" height="10" fill={figureFill} />
        {/* torso */}
        <path
          d="M 22 56 Q 22 50 34 50 L 66 50 Q 78 50 78 56 L 78 180 Q 78 186 72 186 L 28 186 Q 22 186 22 180 Z"
          fill={figureFill}
          stroke={figureStroke}
          strokeWidth="1.5"
        />
        {/* arms */}
        <rect x="6" y="60" width="16" height="100" rx="6" fill={figureFill} stroke={figureStroke} strokeWidth="1.2" />
        <rect x="78" y="60" width="16" height="100" rx="6" fill={figureFill} stroke={figureStroke} strokeWidth="1.2" />
        {/* hips */}
        <rect x="28" y="186" width="44" height="12" fill={figureFill} />
        {/* legs */}
        <rect x="28" y="198" width="18" height="180" rx="6" fill={figureFill} stroke={figureStroke} strokeWidth="1.2" />
        <rect x="54" y="198" width="18" height="180" rx="6" fill={figureFill} stroke={figureStroke} strokeWidth="1.2" />
      </g>

      {/* Crop window overlay (shot framing) */}
      <rect
        x="6"
        y={shot.crop.y}
        width="188"
        height={shot.crop.h}
        fill={cropFill}
        stroke={cropStroke}
        strokeWidth="2.5"
        strokeDasharray="5 3"
        rx="3"
      />

      {/* Frame border */}
      <rect x="1" y="1" width="198" height="398" fill="none" stroke={dark ? "#27272A" : "#D4D4D8"} strokeWidth="2" />
    </svg>
  );
}

// -- Content (EN + ES) ------------------------------------------------------

const CONTENT = {
  en: {
    page_title: "Types of Shots",
    page_sub:
      "The 8 standard shot types every photographer and filmmaker should know — from extreme close-up detail to vast establishing wides. Each shot type changes how viewers feel about your subject.",
    download: "Download as PDF",
    generating: "Generating...",
    intro_title: "Why shot types matter",
    intro_body:
      "Framing is one of the most powerful storytelling tools you have. The same person, in the same outfit, in the same location reads completely differently as an extreme close-up versus a wide shot. Tighter framing creates intimacy and emotion; wider framing places the subject in context and shows scale. Master these 8 standard shot types and you will speak the visual language all photographers, filmmakers, and editors share worldwide.",
    legend_title: "Diagram legend",
    legend_items: [
      { color: "#F5A524", text: "Yellow dashed box — what the camera captures" },
      { color: "#52525B", text: "Gray silhouette — your subject in the scene" },
    ],
    shots: [
      {
        key: "extreme_close_up",
        title: "Extreme Close-Up (ECU)",
        subtitle: "Eyes, mouth, hands — pure detail",
        when: "Use for emotion, texture, or critical detail you want the viewer to focus on completely.",
        tips: [
          "Use a macro or longer lens (85mm+) for flattering compression",
          "Shallow depth of field (f/2 – f/4) keeps attention locked on one detail",
          "Watch for focus — even tiny movement throws the shot off",
        ],
      },
      {
        key: "close_up",
        title: "Close-Up (CU)",
        subtitle: "Head and shoulders — emotional connection",
        when: "Standard portrait framing. Best for showing emotion, character, identity.",
        tips: [
          "85mm – 135mm gives flattering proportions for faces",
          "Eyes are the focal point — focus on the closer eye",
          "Leave a tiny bit of headroom above the hair",
        ],
      },
      {
        key: "medium_close_up",
        title: "Medium Close-Up (MCU)",
        subtitle: "Mid-chest to top of head — interview shot",
        when: "Conversational distance. Common in interviews, news, and dialogue.",
        tips: [
          "Standard 'YouTube' or news-anchor framing",
          "50mm – 85mm works well",
          "Eyes typically sit at the upper third (rule of thirds)",
        ],
      },
      {
        key: "medium_shot",
        title: "Medium Shot (MS)",
        subtitle: "Waist up — balance of subject and context",
        when: "Shows expression and gestures together. The 'cocktail-party' eye level shot.",
        tips: [
          "Hands and body language become part of the story",
          "35mm – 50mm gives a natural feel",
          "Watch the bottom edge — never crop at a joint (waist, wrist)",
        ],
      },
      {
        key: "medium_long_shot",
        title: "Medium Long Shot (MLS)",
        subtitle: "Below knees to top of head — 'American shot'",
        when: "Classic Hollywood/Western framing. Shows full outfit and posture.",
        tips: [
          "Originated in 1930s Hollywood Westerns to show holstered guns",
          "Great for fashion and lifestyle work",
          "Crop just below the knee — never at the knee itself",
        ],
      },
      {
        key: "long_shot",
        title: "Long Shot (LS) / Full Shot",
        subtitle: "Head to toe — full body in context",
        when: "Whole body visible with a small margin. Shows physicality and outfit.",
        tips: [
          "Leave headroom and footroom — don't crop at the hairline or shoes",
          "Subject occupies most of the vertical frame",
          "35mm or wider lets you fit the body without backing up too far",
        ],
      },
      {
        key: "wide_shot",
        title: "Wide Shot (WS)",
        subtitle: "Subject + significant environment",
        when: "Establishes location. The subject is identifiable but the setting is the story.",
        tips: [
          "Use a wide lens (16-35mm full-frame, 10-22mm crop)",
          "Look for leading lines that draw the eye to the subject",
          "Subject should occupy roughly 20–30% of the frame height",
        ],
      },
      {
        key: "extreme_wide_shot",
        title: "Extreme Wide Shot (EWS)",
        subtitle: "Subject is tiny — scale and isolation",
        when: "Establishing shot for landscapes, epic scenes, or when scale itself is the story.",
        tips: [
          "Use 16-24mm full-frame for sweeping vistas",
          "Place the subject on a rule-of-thirds intersection",
          "Watch the horizon line — keep it level",
        ],
      },
    ],
    diagram_label: "Diagram",
    photo_label: "Example photo",
    photo_missing:
      "Photo example coming soon — see diagram for framing reference.",
    footer_note:
      "Shoot the same subject through all 8 framings in one session — you will instantly understand how framing changes the emotional weight of the same moment.",
  },
  es: {
    page_title: "Tipos de Planos / Planos Fotográficos",
    page_sub:
      "Los 8 planos estándar que todo fotógrafo y cineasta debe conocer — desde el detalle extremo hasta los planos generales. Cada tipo de plano cambia como el espectador percibe al sujeto.",
    download: "Descargar como PDF",
    generating: "Generando...",
    intro_title: "Por qué importan los tipos de planos",
    intro_body:
      "El encuadre es una de las herramientas narrativas más poderosas que tienes. La misma persona, con la misma ropa, en la misma ubicación se lee completamente distinta en un primerisimo plano que en un plano general. El encuadre cerrado crea intimidad y emoción; el encuadre abierto situa al sujeto en su contexto y muestra escala. Domina estos 8 planos estándar y hablaras el lenguaje visual que comparten todos los fotógrafos, cineastas y editores en el mundo.",
    legend_title: "Leyenda del diagrama",
    legend_items: [
      { color: "#F5A524", text: "Recuadro amarillo punteado — lo que captura la cámara" },
      { color: "#52525B", text: "Silueta gris — tu sujeto en la escena" },
    ],
    shots: [
      {
        key: "extreme_close_up",
        title: "Primerisimo Plano (PPP)",
        subtitle: "Ojos, boca, manos — detalle puro",
        when: "Para emoción, textura o un detalle crítico en el que quieres concentrar al espectador.",
        tips: [
          "Usa lente macro o teleobjetivo (85mm+) para compresión favorecedora",
          "Profundidad de campo muy reducida (f/2 - f/4) aisla un solo detalle",
          "Cuida el enfoque — el menor movimiento arruina la toma",
        ],
      },
      {
        key: "close_up",
        title: "Primer Plano (PP)",
        subtitle: "Cabeza y hombros — conexión emocional",
        when: "Encuadre clásico de retrato. Ideal para mostrar emoción, carácter, identidad.",
        tips: [
          "85mm - 135mm da proporciones favorecedoras para el rostro",
          "Los ojos son el punto focal — enfoca el ojo más cercano",
          "Deja un mínimo aire sobre la cabeza",
        ],
      },
      {
        key: "medium_close_up",
        title: "Plano Medio Corto (PMC)",
        subtitle: "Del pecho a la cabeza — entrevista",
        when: "Distancia conversacional. Común en entrevistas, noticieros y dialogos.",
        tips: [
          "Encuadre clásico de 'YouTube' o presentador",
          "50mm - 85mm funciona muy bien",
          "Los ojos suelen quedar en el tercio superior (regla de los tercios)",
        ],
      },
      {
        key: "medium_shot",
        title: "Plano Medio (PM)",
        subtitle: "De la cintura a la cabeza — equilibrio sujeto-contexto",
        when: "Muestra expresión y gestos juntos. El plano de 'altura de los ojos' por excelencia.",
        tips: [
          "Las manos y el lenguaje corporal son parte del relato",
          "35mm - 50mm da una sensación natural",
          "Vigila el borde inferior — nunca cortes en una articulación (cintura, muneca)",
        ],
      },
      {
        key: "medium_long_shot",
        title: "Plano Americano (PA)",
        subtitle: "Bajo las rodillas a la cabeza — el plano vaquero",
        when: "Encuadre clásico de Hollywood y los westerns. Muestra el atuendo completo y la postura.",
        tips: [
          "Nacio en los westerns de los 30 para mostrar las pistolas en la cadera",
          "Excelente para moda y estilo de vida",
          "Corta justo debajo de la rodilla — nunca sobre la rodilla misma",
        ],
      },
      {
        key: "long_shot",
        title: "Plano Entero (PE) / Plano Largo",
        subtitle: "De pies a cabeza — cuerpo completo en contexto",
        when: "Cuerpo entero visible con un pequeño margen. Muestra fisicidad y vestuario.",
        tips: [
          "Deja aire arriba y abajo — no cortes en la cabeza ni en los zapatos",
          "El sujeto ocupa la mayor parte del encuadre vertical",
          "Un 35mm o más amplio te permite encuadrar todo el cuerpo sin retroceder demasiado",
        ],
      },
      {
        key: "wide_shot",
        title: "Plano General (PG)",
        subtitle: "Sujeto + entorno significativo",
        when: "Establece la ubicación. El sujeto se identifica pero el entorno es la historia.",
        tips: [
          "Usa gran angular (16-35mm full-frame, 10-22mm APS-C)",
          "Busca líneas guía que dirijan la mirada al sujeto",
          "El sujeto debe ocupar entre el 20 y 30% de la altura del encuadre",
        ],
      },
      {
        key: "extreme_wide_shot",
        title: "Gran Plano General (GPG)",
        subtitle: "El sujeto es diminuto — escala y aislamiento",
        when: "Plano de establecimiento para paisajes, escenas epicas o cuando la escala es la historia.",
        tips: [
          "Usa 16-24mm full-frame para vistas amplias",
          "Coloca al sujeto en una intersección de la regla de los tercios",
          "Cuida la línea del horizonte — mantenla nivelada",
        ],
      },
    ],
    diagram_label: "Diagrama",
    photo_label: "Foto de ejemplo",
    photo_missing:
      "Foto de ejemplo próxima — consulta el diagrama para el encuadre.",
    footer_note:
      "Fotografía al mismo sujeto con los 8 encuadres en una sola sesión — entenderas al instante como el encuadre cambia el peso emocional del mismo momento.",
  },
};

export default function ShotsPage() {
  const { lang } = useTranslation();
  const c = CONTENT[lang] || CONTENT.en;
  const printRef = useRef(null);

  const { ready, generating, download } = usePDFExport(
    printRef,
    () => `types-of-shots-${lang}.pdf`,
    [lang]
  );

  // Merge content with diagram metadata.
  const shotsMerged = c.shots.map((s) => {
    const meta = SHOTS.find((m) => m.key === s.key);
    return { ...s, ...meta };
  });

  return (
    <div
      className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 print:bg-white print:text-black"
      data-testid="shots-page"
    >
      {/* Screen */}
      <div className="print:hidden">
        <motion.div variants={container} initial="hidden" animate="show" className="space-y-8">
          <motion.div variants={itemAnim} className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold text-white tracking-tight mb-2">
                {c.page_title}
              </h1>
              <p className="text-[#71717A] text-base max-w-3xl">{c.page_sub}</p>
            </div>
            <Button
              onClick={download}
              disabled={!ready}
              className="bg-[#F5A524] text-[#0A0A0A] hover:bg-[#F5B041] font-medium disabled:opacity-60"
              data-testid="shots-download-btn"
            >
              <Printer className="w-4 h-4 mr-1.5" />
              {generating ? c.generating : c.download}
            </Button>
          </motion.div>

          {/* Intro */}
          <motion.div
            variants={itemAnim}
            className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6"
          >
            <div className="flex items-center gap-2 mb-2">
              <FilmStrip weight="fill" className="w-5 h-5 text-[#F5A524]" />
              <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.intro_title}</h2>
            </div>
            <p className="text-[#A1A1AA] text-sm leading-relaxed">{c.intro_body}</p>

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
              <p className="text-[#71717A] text-xs uppercase tracking-widest font-semibold col-span-full">
                {c.legend_title}
              </p>
              {c.legend_items.map((item, i) => (
                <div key={i} className="flex items-center gap-2 text-[#A1A1AA] text-sm">
                  <span
                    className="w-3.5 h-3.5 rounded-sm border-2 shrink-0"
                    style={{ borderColor: item.color, background: `${item.color}30` }}
                  />
                  {item.text}
                </div>
              ))}
            </div>
          </motion.div>

          {/* Shot cards */}
          {shotsMerged.map((shot, i) => (
            <motion.div
              key={shot.key}
              variants={itemAnim}
              className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6"
              data-testid={`shot-card-${shot.key}`}
            >
              <div className="flex items-start justify-between gap-3 mb-1">
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-full bg-[#F5A524]/15 text-[#F5A524] flex items-center justify-center text-sm font-bold font-['JetBrains_Mono']">
                    {i + 1}
                  </span>
                  <div>
                    <h2 className="font-['Outfit'] text-white text-xl font-medium">{shot.title}</h2>
                    <p className="text-[#71717A] text-xs">{shot.subtitle}</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mt-4">
                {/* Diagram */}
                <div className="md:col-span-3">
                  <p className="text-[#71717A] text-[10px] uppercase tracking-widest font-semibold mb-2">
                    {c.diagram_label}
                  </p>
                  <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-3">
                    <ShotDiagram shot={shot} dark />
                  </div>
                </div>

                {/* Photo */}
                <div className="md:col-span-5">
                  <p className="text-[#71717A] text-[10px] uppercase tracking-widest font-semibold mb-2">
                    {c.photo_label}
                  </p>
                  <div className="bg-[#0A0A0A] border border-white/10 rounded-xl overflow-hidden aspect-[16/9] flex items-center justify-center">
                    {shot.photo ? (
                      <img
                        src={shot.photo}
                        alt={shot.title}
                        className="w-full h-full object-cover"
                        crossOrigin="anonymous"
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.parentElement.innerHTML = `<div class='text-[#71717A] text-xs italic p-4 text-center'>${c.photo_missing}</div>`;
                        }}
                      />
                    ) : (
                      <div className="text-[#71717A] text-xs italic p-4 text-center">
                        {c.photo_missing}
                      </div>
                    )}
                  </div>
                </div>

                {/* Description */}
                <div className="md:col-span-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Eye weight="fill" className="w-4 h-4 text-[#F5A524]" />
                    <p className="text-white text-sm font-medium">{shot.when}</p>
                  </div>
                  <ul className="space-y-1.5 mt-3">
                    {shot.tips.map((tip, j) => (
                      <li key={j} className="flex items-start gap-2">
                        <span className="mt-1.5 w-1 h-1 rounded-full bg-[#F5A524] shrink-0" />
                        <span className="text-[#A1A1AA] text-xs leading-relaxed">{tip}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          ))}

          <motion.p
            variants={itemAnim}
            className="text-[#A1A1AA] text-sm italic text-center max-w-3xl mx-auto leading-relaxed"
          >
            "{c.footer_note}"
          </motion.p>
        </motion.div>
      </div>

      {/* Print / PDF view */}
      <div ref={printRef} className="hidden print:block bg-white text-black p-4" data-testid="shots-print-view">
        <h1 className="text-3xl font-bold text-center mb-1">{c.page_title}</h1>
        <p className="text-center text-gray-500 mb-5 text-sm">{c.page_sub}</p>

        <div className="bg-gray-50 rounded-lg p-4 mb-6 border border-gray-200">
          <h2 className="text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">
            {c.intro_title}
          </h2>
          <p className="text-sm text-gray-700 leading-relaxed">{c.intro_body}</p>
        </div>

        {shotsMerged.map((shot, i) => (
          <div key={shot.key} className="mb-6 break-inside-avoid">
            <h2 className="text-xl font-bold mb-1 border-b-2 border-gray-200 pb-2">
              {i + 1}. {shot.title}
            </h2>
            <p className="text-xs text-gray-500 italic mb-2">{shot.subtitle}</p>
            <div className="grid grid-cols-3 gap-3 mb-2">
              <div className="bg-gray-50 rounded p-2 border border-gray-200">
                <ShotDiagram shot={shot} dark={false} />
              </div>
              <div className="col-span-2 bg-gray-50 rounded overflow-hidden border border-gray-200 aspect-[16/9] flex items-center justify-center">
                {shot.photo ? (
                  <img
                    src={shot.photo}
                    alt={shot.title}
                    className="w-full h-full object-cover"
                    crossOrigin="anonymous"
                  />
                ) : (
                  <span className="text-xs text-gray-500 italic p-2 text-center">{c.photo_missing}</span>
                )}
              </div>
            </div>
            <p className="text-sm text-gray-800 mb-2">
              <strong>{c.diagram_label}:</strong> {shot.when}
            </p>
            <ul className="list-disc ml-5 space-y-1">
              {shot.tips.map((tip, j) => (
                <li key={j} className="text-xs text-gray-700">
                  {tip}
                </li>
              ))}
            </ul>
          </div>
        ))}

        <p className="text-sm text-gray-600 italic text-center max-w-2xl mx-auto leading-relaxed mt-8">
          "{c.footer_note}"
        </p>
        <p className="text-center text-xs text-gray-400 mt-4">www.dslradvisor.com</p>
      </div>
    </div>
  );
}
